import Route from '@ember/routing/route';
import { service } from '@ember/service';
import {
  isPslfDebugActive,
  getPslfDebugState,
  buildPslfDataOverrides,
  buildMockLoanSummary,
} from 'tio-employee/utils/pslf-debug-scenarios';
import type { PslfDebugState } from 'tio-employee/utils/pslf-debug-scenarios';
import type PersonModel from 'tio-common/models/person';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type { NsldsStructuredContent } from 'tio-common/models/observable-document';
import type RecommendationModel from 'tio-common/models/recommendation';
import type StoreService from 'ember-data/store';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';

export interface PslfDebugOverrides {
  linkLoans: boolean;
}

export default class PslfIndexRoute extends Route {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;

    const [pslfData, loanSummary, person, mostRecentRecQuery, nsldsDocuments] = await Promise.all([
      this.reimaginedData.fetchPslfData(),
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      this.store.adapterFor('user').getLoanSummary(userId),
      // @ts-expect-error type models
      this.store.findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans',
        reload: true,
      }),
      this.store.query<RecommendationModel>('recommendation', {
        filter: {
          user: userId,
          'requesting-app': 'PSLF',
        },
        include: 'user',
        sort: '-created-at',
        page: {
          offset: 0,
          limit: 1,
        },
      }),
      this.store.query<ObservableDocumentModel>('observable-document', {
        filter: {
          person: personId,
          provider: 'ObservableProvider.NSLDS',
        },
        sort: '-created-at',
        page: { limit: 1 },
      }),
    ]);

    // Extract file_request_date from the most recent NSLDS document
    let nsldsFileRequestDate: string | null = null;
    const mostRecentNslds = nsldsDocuments?.[0];
    if (mostRecentNslds) {
      const content = mostRecentNslds.structuredContent as NsldsStructuredContent | undefined;
      nsldsFileRequestDate = content?.file?.file_request_date ?? null;
    }

    const result = {
      pslfData,
      loanSummary,
      person,
      nsldsFileRequestDate,
      mostRecentRecommendation: mostRecentRecQuery?.[0] as RecommendationModel | undefined,
      debugOverrides: undefined as PslfDebugOverrides | undefined,
    };

    // Apply PSLF debug overrides when ?pslf_debug=true
    if (isPslfDebugActive()) {
      const debugState = getPslfDebugState();

      result.pslfData = buildPslfDataOverrides(debugState, pslfData);
      result.loanSummary = buildMockLoanSummary(debugState);

      if (debugState.estimatorCompleted || debugState.recertifyIncome) {
        result.mostRecentRecommendation = {
          estimatedMonthlyPayment: 285,
          estimatedForgivenessAmount: 62340,
        } as unknown as RecommendationModel;
      } else {
        result.mostRecentRecommendation = undefined;
      }

      if (debugState.hasNsldsData || debugState.annualReminder) {
        result.nsldsFileRequestDate = '2025-01-15';
      }

      // Push mock Account/Loan records into the Ember Data store so that
      // person.accounts naturally returns our debug data. Child components
      // stay completely agnostic to the debug mechanism.
      if (debugState.hasNsldsData || debugState.annualReminder) {
        this.pushDebugAccountsIntoStore(person, debugState);
      }

      result.debugOverrides = {
        linkLoans: debugState.linkLoans,
      };
    }

    return result;
  }

  /**
   * Push mock Account and Loan records into the Ember Data store so that
   * person.accounts returns debug data. Unloads existing accounts first
   * to avoid mixing real and mock data.
   */
  private pushDebugAccountsIntoStore(person: PersonModel, debugState: PslfDebugState) {
    const accountId = `debug-nslds-${person.id}`;

    // Unload existing accounts to avoid mixing live and debug data
    const existingAccounts = person.accounts?.slice() ?? [];
    for (const account of existingAccounts) {
      for (const loan of account.loans?.slice() ?? []) {
        loan.unloadRecord();
      }
      account.unloadRecord();
    }

    // Build JSON:API loan resources
    const loanResources = debugState.debugLoans.map((debugLoan, i) => ({
      id: `debug-loan-${i + 1}`,
      type: 'loan',
      attributes: {
        isActive: true,
        observation: {
          'loan-name': debugLoan.name,
          balance: debugLoan.currentBalance,
          'principal-balance': debugLoan.currentBalance,
          'interest-rate': 5.5,
          'forgiveness-program-qualified': debugLoan.qualifyingPayments,
          'is-paid-off': false,
          'loan-type': debugLoan.loanType,
          'repayment-plan-derived': 'ibr',
          status: 'active',
        },
      },
      relationships: {
        account: { data: { type: 'account', id: accountId } },
      },
    }));

    // Push account + loans into the store

    this.store.push({
      data: {
        id: accountId,
        type: 'account',
        attributes: {
          isActive: true,
          observation: {
            provider: 'ObservableProvider.NSLDS',
            balance: debugState.debugLoans.reduce((sum, l) => sum + l.currentBalance, 0),
          },
        },
        relationships: {
          person: { data: { type: 'person', id: person.id } },
          loans: {
            data: loanResources.map((l) => ({ type: 'loan', id: l.id })),
          },
        },
      },
      included: loanResources,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }
}
